import { ReactNode, useEffect, useRef, useState } from 'react';
import { DefaultValues, FieldValues, UseFormReturn } from 'react-hook-form';

import { PunchupSpinner } from 'features/common/components/Punchup/primitives/feedback/PunchupSpinner';

import styles from './useSeededEditForm.module.css';

export interface UseSeededEditFormParams<TData, TForm extends FieldValues> {
	/**
	 * false → create mode: nothing to load, the form is ready on first render.
	 * true  → edit mode: the form is held behind the spinner until `seedData` arrives.
	 */
	isEditMode: boolean;
	formMethods: UseFormReturn<TForm>;
	/**
	 * The already-fetched server entity (e.g. a react-query `data`). Seeding fires exactly
	 * once, when this first becomes non-null. Pass `undefined`/`null` while loading.
	 */
	seedData: TData | undefined | null;
	/**
	 * Maps the server entity into the values to reset the form to. Called once, at seed time.
	 * Returns `DefaultValues<TForm>` (a deep-partial) — fields the form doesn't edit may be omitted,
	 * matching what react-hook-form's `reset` accepts. Keep it pure.
	 */
	toFormValues: (data: TData) => DefaultValues<TForm>;
	/** Optional one-time side effect run right after the reset (e.g. snapshot a baseline ref). */
	onSeeded?: (data: TData) => void;
	/** When the fetch errors, release the gate (render with defaults) instead of spinning forever. */
	isError?: boolean;
	/** Spinner size for the gate. Defaults to 32 (the inline-form overlay convention). */
	spinnerSize?: number;
}

export interface UseSeededEditFormResult {
	/** true once the form is ready: immediately in create mode; after the seed (or a fetch error) in edit mode. */
	isSeeded: boolean;
	/** Centered spinner to render while not seeded — `if (!isSeeded) return seedGate;` (edit mode only). */
	seedGate: ReactNode;
}

/**
 * useSeededEditForm
 *
 * Shared seed-once + loading-gate orchestration for accordion edit forms. Composes with
 * (does NOT replace) `useAccordionForm` — a form calls both. It owns the two pieces every
 * edit form was re-implementing by hand:
 *
 *   1. Seed-once guard — `reset(toFormValues(seedData))` runs exactly once when the server
 *      data first arrives, so a later refetch can't clobber the user's in-progress edits.
 *   2. Loading gate — `isSeeded` is false in edit mode until the seed lands; render `seedGate`
 *      (a centered spinner) while `!isSeeded` so radios/toggles/selects never paint hardcoded
 *      defaults for a frame and then re-select (the "blink").
 *
 * Create mode is seeded from the first render (nothing to load), so the gate is a no-op there.
 * It is value-driven (keys off `seedData`, not a query `onSuccess`), so it works whether the
 * caller seeds from react-query, an imperative fetch stored in state, or any other source.
 *
 * See `src/styles/docs/form-persistence.md` → "Seeding the form once".
 */
export const useSeededEditForm = <TData, TForm extends FieldValues>({
	isEditMode,
	formMethods,
	seedData,
	toFormValues,
	onSeeded,
	isError = false,
	spinnerSize = 32,
}: UseSeededEditFormParams<TData, TForm>): UseSeededEditFormResult => {
	// Create mode has nothing to load → ready immediately. Edit mode waits for the seed.
	const [isSeeded, setIsSeeded] = useState(!isEditMode);
	const hasReset = useRef(false);

	useEffect(() => {
		if (hasReset.current || !isEditMode) return;
		if (seedData != null) {
			formMethods.reset(toFormValues(seedData));
			onSeeded?.(seedData);
			hasReset.current = true;
			setIsSeeded(true);
		} else if (isError) {
			// Fail open: release the form with its defaults rather than trapping on the spinner.
			hasReset.current = true;
			setIsSeeded(true);
		}
	}, [seedData, isError, isEditMode, formMethods, toFormValues, onSeeded]);

	const seedGate = (
		<div className={styles.seedGate}>
			<PunchupSpinner size={spinnerSize} />
		</div>
	);

	return { isSeeded, seedGate };
};
